import api from "./api";
import { ResearchReport } from "../interface/ResearchReport";
import { ResearchReportID } from "../interface/ResearchReportID";
import { ResearchDocument } from "@/interface";

interface ResearchQuery {
  requesterId?: string;
  requesterRole?: string;
  page?: number;
  size?: number;
  type?: string;
  search?: string;
  status?: string;
}

const toQuery = (query: ResearchQuery = {}) => {
  const params = new URLSearchParams();

  if (query.requesterId !== undefined) params.set("requesterId", query.requesterId);
  if (query.requesterRole !== undefined) params.set("requesterRole", query.requesterRole);
  if (query.page !== undefined) params.set("page", String(query.page));
  if (query.size !== undefined) params.set("size", String(query.size));
  if (query.type !== undefined) params.set("type", query.type);
  if (query.search !== undefined) params.set("search", query.search);
  if (query.status !== undefined) params.set("status", query.status);
  return params.toString();
};

/**
 * Research Service
 * Handles API interactions for Research Reports
 */
export const researchService = {
  /**
   * Publish a new research report
   * @param {ResearchReport} report - The research report to publish
   * @returns {Promise<any>}
   */
  async publishResearch(report: ResearchReport): Promise<any> {
    return api.post("/research", report);
  },

  /**
   * Get research reports
   * @returns {Promise<any>}
   */
  async getResearchReports(query: ResearchQuery = {}): Promise<any> {
    const queryString = toQuery(query);
    return api.get(`/research${queryString ? `?${queryString}` : ""}`);
  },

  async updateResearch(id: string, report: Partial<ResearchReportID>): Promise<any> {
    return api.put(`/research/${id}`, report);
  },

  async addResearchDocuments(id, documents): Promise<any> {
    return api.post(`/research/${id}/documents`,  documents );
  },

  async deleteResearch(id, authorId): Promise<any> {
    const queryString = `authorId=${authorId}`;
    return api.delete(`/research/${id}${queryString ? `?${queryString}` : ""}`);
  },

  /**
   * DELETE /research/recipient/{id}?authorId=&recipientId=
   * Permanently deletes a draft research item.
   */
  async deleteResearchRecipient(id, authorId, recipientId): Promise<any> {
    const queryString = `authorId=${authorId}&recipientId=${recipientId}`;
    return api.delete(`/research/${id}/recipient${queryString ? `?${queryString}` : ""}`);
  },

  async deleteAttachment(id, attachmentId, authorId): Promise<any> {
    const queryString = `authorId=${authorId}`;
    return api.delete(`/research/${id}/attachments/${attachmentId}${queryString ? `?${queryString}` : ""}`);
  },

  async updateReadViews(id: string, viewerId: string): Promise<any> {
    const params = new URLSearchParams({ viewerId });
    return api.post(`/research/${id}/read?${params.toString()}`, {});
  },

  async updateResearchStatus(id, status): Promise<any> {
    const queryString = `status=${status}`;
    return api.put(`/research/${id}/status?${queryString}`, {});
  }
};

export default researchService;
